import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  useApi,
  useFetcher,
  type ArtifactSummary,
  type Deployment,
  type DeploymentPlan,
  type Environment,
  type Page,
  type Pipeline,
  type PipelineDetail,
  type Project as ProjectType,
  type ProjectStatus,
  type SecretMetadata,
  type Target,
} from "../api.js";
import { useOrg } from "../components/Layout.js";
import { Button, Card, Empty, ErrorBox, Loading, PageHeader, Sha, StatusBadge, Tabs, Time, useAction } from "../components/ui.js";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "pipelines", label: "Pipelines" },
  { id: "deployments", label: "Deployments" },
  { id: "targets", label: "Targets" },
  { id: "environments", label: "Environments" },
  { id: "artifacts", label: "Artifacts" },
  { id: "database", label: "Database" },
  { id: "secrets", label: "Secrets" },
];

export function ProjectPage() {
  const org = useOrg();
  const { project: slug = "", tab = "overview" } = useParams();
  const project = useApi<ProjectType>(`/v1/orgs/${org}/projects/${slug}`);
  const [dialog, setDialog] = useState<"deploy" | "ci" | null>(null);
  const base = `/o/${org}/projects/${slug}`;

  if (project.loading) return <Loading />;
  if (project.error || !project.data) return <ErrorBox error={project.error ?? new Error("Project not found")} />;
  const p = project.data;
  return (
    <>
      <PageHeader
        title={p.slug}
        subtitle={
          <>
            {p.product} · {p.repository}
            {p.latestConfig && (
              <>
                {" "}
                · repu.yml at <Sha sha={p.latestConfig.commitSha} /> ({p.latestConfig.ref})
              </>
            )}
          </>
        }
        actions={
          <>
            <Button onClick={() => { setDialog("ci"); }} disabled={!p.latestConfig}>
              Run CI
            </Button>
            <Button variant="primary" onClick={() => { setDialog("deploy"); }} disabled={!p.latestConfig}>
              Deploy
            </Button>
          </>
        }
      />
      {dialog === "deploy" && p.latestConfig && <DeployForm project={p} commitSha={p.latestConfig.commitSha} gitRef={p.latestConfig.ref} onClose={() => { setDialog(null); }} />}
      {dialog === "ci" && p.latestConfig && <RunCiForm project={p} commitSha={p.latestConfig.commitSha} gitRef={p.latestConfig.ref} onClose={() => { setDialog(null); }} />}
      <Tabs tabs={TABS} active={tab} base={base} />
      {tab === "overview" && <Overview slug={slug} />}
      {tab === "pipelines" && <PipelinesTab slug={slug} />}
      {tab === "deployments" && <DeploymentsTab slug={slug} />}
      {tab === "targets" && <TargetsTab slug={slug} />}
      {tab === "environments" && <EnvironmentsTab slug={slug} />}
      {tab === "artifacts" && <ArtifactsTab slug={slug} />}
      {tab === "database" && <DatabaseTab slug={slug} />}
      {tab === "secrets" && <SecretsTab slug={slug} />}
    </>
  );
}

function Overview({ slug }: { slug: string }) {
  const org = useOrg();
  const status = useApi<ProjectStatus>(`/v1/orgs/${org}/projects/${slug}/status`, { refreshMs: 10_000 });
  if (status.loading) return <Loading />;
  return (
    <>
      <ErrorBox error={status.error} />
      <div className="grid">
        {status.data?.environments.map((env) => (
          <Card key={env.name} title={<>{env.name}{env.protected && <span className="tag">protected</span>}</>}>
            {env.latest ? <DeploymentSummary deployment={env.latest} /> : <p className="muted">Never deployed.</p>}
            {env.lastSucceeded && env.lastSucceeded.id !== env.latest?.id && (
              <p className="muted small">
                Live: <Link to={`/o/${org}/deployments/${env.lastSucceeded.id}`}><Sha sha={env.lastSucceeded.commitSha} /></Link> <Time iso={env.lastSucceeded.updatedAt} />
              </p>
            )}
            {env.active.length > 1 && <p className="muted small">{env.active.length} deployments in progress or waiting</p>}
          </Card>
        ))}
      </div>
    </>
  );
}

function DeploymentSummary({ deployment }: { deployment: Deployment }) {
  const org = useOrg();
  return (
    <Link className="deployment-summary" to={`/o/${org}/deployments/${deployment.id}`}>
      <StatusBadge status={deployment.status} />
      <span>
        <Sha sha={deployment.commitSha} /> {deployment.ref}
      </span>
      <span className="muted">
        {deployment.targets.length} target{deployment.targets.length === 1 ? "" : "s"} · <Time iso={deployment.updatedAt} />
      </span>
    </Link>
  );
}

export function PipelinesTable({ pipelines, org }: { pipelines: Pipeline[]; org: string }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Status</th>
          <th>Pipeline</th>
          <th>Commit</th>
          <th>Trigger</th>
          <th>Targets</th>
          <th>Started</th>
        </tr>
      </thead>
      <tbody>
        {pipelines.map((p) => (
          <tr key={p.id}>
            <td>
              <StatusBadge status={p.status} />
            </td>
            <td>
              <Link to={`/o/${org}/pipelines/${p.id}`}>{p.mode === "deploy" ? `deploy → ${p.environment ?? ""}` : "ci"}</Link>
            </td>
            <td>
              <Sha sha={p.commitSha} /> <span className="muted">{p.ref}</span>
            </td>
            <td>{p.trigger.type}</td>
            <td className="muted">{p.targets.join(", ")}</td>
            <td>
              <Time iso={p.createdAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PipelinesTab({ slug }: { slug: string }) {
  const org = useOrg();
  const list = useApi<Page<Pipeline>>(`/v1/orgs/${org}/projects/${slug}/pipelines?limit=50`, { refreshMs: 10_000 });
  if (list.loading) return <Loading />;
  if (list.data?.items.length === 0) return <Empty title="No pipelines yet">Push to GitHub or use Run CI.</Empty>;
  return (
    <>
      <ErrorBox error={list.error} />
      {list.data && <PipelinesTable pipelines={list.data.items} org={org} />}
    </>
  );
}

function DeploymentsTab({ slug }: { slug: string }) {
  const org = useOrg();
  const [environment, setEnvironment] = useState("");
  const list = useApi<Page<Deployment>>(`/v1/orgs/${org}/projects/${slug}/deployments?limit=50${environment ? `&environment=${environment}` : ""}`, { refreshMs: 10_000 });
  const envs = useApi<Page<Environment>>(`/v1/orgs/${org}/projects/${slug}/environments`);
  return (
    <>
      <div className="toolbar">
        <label>
          Environment{" "}
          <select value={environment} onChange={(e) => { setEnvironment(e.target.value); }}>
            <option value="">All</option>
            {envs.data?.items.map((e) => (
              <option key={e.name}>{e.name}</option>
            ))}
          </select>
        </label>
      </div>
      <ErrorBox error={list.error} />
      {list.loading && <Loading />}
      {list.data?.items.length === 0 && <Empty title="No deployments" />}
      {list.data && list.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Environment</th>
              <th>Commit</th>
              <th>Targets</th>
              <th>Requested by</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((d) => (
              <tr key={d.id}>
                <td>
                  <Link to={`/o/${org}/deployments/${d.id}`}>
                    <StatusBadge status={d.status} />
                  </Link>
                </td>
                <td>{d.environment}</td>
                <td>
                  <Sha sha={d.commitSha} /> <span className="muted">{d.ref}</span>
                </td>
                <td className="muted">{d.targets.map((t) => t.address).join(", ")}</td>
                <td>{d.requestedBy.type === "user" ? "user" : d.requestedBy.type}</td>
                <td>
                  <Time iso={d.updatedAt} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function TargetsTab({ slug }: { slug: string }) {
  const org = useOrg();
  const targets = useApi<Page<Target>>(`/v1/orgs/${org}/projects/${slug}/targets`);
  if (targets.loading) return <Loading />;
  if (targets.error) return <ErrorBox error={targets.error} />;
  const depth = (t: Target): number => (t.parent ? 1 + depth(targets.data?.items.find((x) => x.address === t.parent) ?? { ...t, parent: null }) : 0);
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Target</th>
          <th>Kind</th>
          <th>Deploys to</th>
          <th>Runner</th>
          <th>Stages</th>
          <th>Depends on</th>
        </tr>
      </thead>
      <tbody>
        {targets.data?.items.map((t) => (
          <tr key={t.address}>
            <td style={{ paddingLeft: `${0.75 + depth(t) * 1.25}rem` }}>
              <code>{t.parent ? t.address.split("/").pop() : t.address}</code>
            </td>
            <td>{t.kind}</td>
            <td>{t.deploy ? t.destination : <span className="muted">—</span>}</td>
            <td>{t.runner.os}</td>
            <td className="muted">{t.stages.join(" · ") || "—"}</td>
            <td className="muted">{t.dependsOn.join(", ") || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EnvironmentsTab({ slug }: { slug: string }) {
  const org = useOrg();
  const envs = useApi<Page<Environment>>(`/v1/orgs/${org}/projects/${slug}/environments`);
  if (envs.loading) return <Loading />;
  return (
    <>
      <ErrorBox error={envs.error} />
      <div className="grid">
        {envs.data?.items.map((e) => (
          <Card key={e.name} title={e.name}>
            <dl className="facts">
              <dt>Flags</dt>
              <dd>{[e.primary && "primary", e.protected && "protected", e.ephemeral && "ephemeral"].filter(Boolean).join(", ") || "—"}</dd>
              <dt>Branches</dt>
              <dd>{e.branches.join(", ") || "any"}</dd>
              <dt>Approval</dt>
              <dd>{e.approvalRequired ? "required" : "not required"}</dd>
              <dt>Targets</dt>
              <dd>{e.targets.join(", ")}</dd>
              <dt>Domains</dt>
              <dd>
                {e.domains.length === 0
                  ? "—"
                  : e.domains.map((d) => (
                      <div key={d.hostname}>
                        {d.hostname} <span className="muted">→ {d.target}</span>
                      </div>
                    ))}
              </dd>
            </dl>
          </Card>
        ))}
      </div>
    </>
  );
}

function ArtifactsTab({ slug }: { slug: string }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const artifacts = useApi<Page<ArtifactSummary>>(`/v1/orgs/${org}/projects/${slug}/artifacts`);
  const download = useAction(async (id: string) => {
    const url = await fetcher<{ url: string }>(`/v1/orgs/${org}/artifacts/${id}/download-url`, { method: "POST" });
    window.location.assign(url.url);
  });
  if (artifacts.loading) return <Loading />;
  if (artifacts.data?.items.length === 0) return <Empty title="No artifacts yet">Artifacts are created by deploy pipelines.</Empty>;
  return (
    <>
      <ErrorBox error={artifacts.error ?? download.error} />
      <table className="table">
        <thead>
          <tr>
            <th>Target</th>
            <th>Kind</th>
            <th>Commit</th>
            <th>Size</th>
            <th>SHA-256</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {artifacts.data?.items.map((a) => (
            <tr key={a.id}>
              <td>{a.target}</td>
              <td>{a.kind}</td>
              <td>
                <Sha sha={a.commitSha} />
              </td>
              <td>{(a.sizeBytes / 1024).toFixed(1)} KiB</td>
              <td>
                <code className="muted">{a.sha256.slice(0, 12)}</code>
              </td>
              <td>
                <Time iso={a.createdAt} />
              </td>
              <td>
                {a.status === "available" ? (
                  <button className="link-button" onClick={() => { void download.run(a.id); }}>
                    Download
                  </button>
                ) : (
                  <StatusBadge status={a.status} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DatabaseTab({ slug }: { slug: string }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const envs = useApi<Page<Environment>>(`/v1/orgs/${org}/projects/${slug}/environments`);
  const [environment, setEnvironment] = useState("");
  const [mode, setMode] = useState<"sample" | "full">("sample");
  const start = useAction(async (kind: "checks" | "migrations", dryRun: boolean) => {
    const p = await fetcher<PipelineDetail>(`/v1/orgs/${org}/projects/${slug}/database/${kind}`, { method: "POST", body: { environment, mode, dryRun } });
    void navigate(`/o/${org}/pipelines/${p.id}`);
  });
  const env = envs.data?.items.find((e) => e.name === environment);
  return (
    <Card title="Schema checks and migrations">
      <p className="muted">
        Runs as a CI job with the environment's credentials, which never leave Forge. Checks compare <code>database/schema</code> with the data;
        migrations apply <code>database/migrations</code> in order.
      </p>
      <div className="form inline-form">
        <label>
          Environment
          <select value={environment} onChange={(e) => { setEnvironment(e.target.value); }}>
            <option value="">Choose…</option>
            {envs.data?.items.filter((e) => !e.ephemeral).map((e) => (
              <option key={e.name} value={e.name}>
                {e.name}
                {e.protected ? " (protected)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Check scope
          <select value={mode} onChange={(e) => { setMode(e.target.value as "sample" | "full"); }}>
            <option value="sample">Sample (1,000 per collection)</option>
            <option value="full">Full scan (reads every document)</option>
          </select>
        </label>
        <ErrorBox error={start.error} />
        <div className="form-actions">
          <Button disabled={!environment} busy={start.busy} onClick={() => { void start.run("checks", true); }}>
            Check schema
          </Button>
          <Button disabled={!environment} busy={start.busy} onClick={() => { void start.run("migrations", true); }}>
            Migration dry run
          </Button>
          <Button
            variant="danger"
            disabled={!environment}
            busy={start.busy}
            onClick={() => {
              if (window.confirm(`Apply pending migrations to ${environment}${env?.protected ? " (protected)" : ""}? Data migrations are not reversible.`)) void start.run("migrations", false);
            }}
          >
            Apply migrations
          </Button>
        </div>
      </div>
    </Card>
  );
}

function SecretsTab({ slug }: { slug: string }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const secrets = useApi<Page<SecretMetadata>>(`/v1/orgs/${org}/projects/${slug}/secrets`);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [environment, setEnvironment] = useState("*");
  const save = useAction(
    async () => {
      await fetcher(`/v1/orgs/${org}/projects/${slug}/secrets/${name}`, { method: "PUT", body: { value, environment } });
      setValue("");
      setName("");
    },
    () => {
      secrets.reload();
    },
  );
  const remove = useAction(
    async (n: string, env: string) => {
      await fetcher(`/v1/orgs/${org}/projects/${slug}/secrets/${n}?environment=${encodeURIComponent(env)}`, { method: "DELETE" });
    },
    () => {
      secrets.reload();
    },
  );
  return (
    <>
      <Card title="Add or replace a secret">
        <form
          className="form inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save.run();
          }}
        >
          <label>
            Name
            <input value={name} onChange={(e) => { setName(e.target.value.toUpperCase()); }} required pattern="[A-Z][A-Z0-9_]*" placeholder="FIREBASE_SERVICE_ACCOUNT" />
          </label>
          <label>
            Environment
            <input value={environment} onChange={(e) => { setEnvironment(e.target.value); }} required placeholder="* or production" />
          </label>
          <label className="wide">
            Value
            <textarea value={value} onChange={(e) => { setValue(e.target.value); }} required rows={3} autoComplete="off" spellCheck={false} />
          </label>
          <ErrorBox error={save.error} />
          <div className="form-actions">
            <Button variant="primary" type="submit" busy={save.busy}>
              Save secret
            </Button>
          </div>
          <p className="muted small">Values are encrypted and never shown again. Only jobs that need them receive them.</p>
        </form>
      </Card>
      <ErrorBox error={secrets.error ?? remove.error} />
      {secrets.data && secrets.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Environment</th>
              <th>Version</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {secrets.data.items.map((s) => (
              <tr key={`${s.name}:${s.environment}`}>
                <td>
                  <code>{s.name}</code>
                </td>
                <td>{s.environment === "*" ? "all" : s.environment}</td>
                <td>{s.version}</td>
                <td>
                  <Time iso={s.updatedAt} />
                </td>
                <td>
                  <button className="link-button danger" onClick={() => { if (window.confirm(`Delete ${s.name} (${s.environment})?`)) void remove.run(s.name, s.environment); }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function DeployForm({ project, commitSha, gitRef, onClose }: { project: ProjectType; commitSha: string; gitRef: string; onClose: () => void }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const envs = useApi<Page<Environment>>(`/v1/orgs/${org}/projects/${project.slug}/environments`);
  const [environment, setEnvironment] = useState("");
  const [sha, setSha] = useState(commitSha);
  const [ref, setRef] = useState(gitRef.replace(/^refs\/heads\//, ""));
  const [targets, setTargets] = useState("");
  const [plan, setPlan] = useState<DeploymentPlan>();
  const body = () => ({ environment, commitSha: sha, ref, ...(targets.trim() ? { targets: targets.split(",").map((t) => t.trim()).filter(Boolean) } : {}) });
  const preview = useAction(async () => {
    setPlan(await fetcher<DeploymentPlan>(`/v1/orgs/${org}/projects/${project.slug}/deployments`, { method: "POST", body: { ...body(), dryRun: true } }));
  });
  const deploy = useAction(async () => {
    const d = await fetcher<Deployment>(`/v1/orgs/${org}/projects/${project.slug}/deployments`, { method: "POST", body: body() });
    void navigate(`/o/${org}/deployments/${d.id}`);
  });
  return (
    <Card title="Deploy" actions={<button className="link-button" onClick={onClose}>Close</button>}>
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void preview.run();
        }}
      >
        <label>
          Environment
          <select value={environment} onChange={(e) => { setEnvironment(e.target.value); setPlan(undefined); }} required>
            <option value="">Choose…</option>
            {envs.data?.items.filter((e) => !e.ephemeral).map((e) => (
              <option key={e.name} value={e.name}>
                {e.name}
                {e.protected ? " (protected)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Commit
          <input value={sha} onChange={(e) => { setSha(e.target.value); setPlan(undefined); }} required pattern="[0-9a-f]{40}" />
        </label>
        <label>
          Branch
          <input value={ref} onChange={(e) => { setRef(e.target.value); setPlan(undefined); }} required />
        </label>
        <label>
          Targets
          <input value={targets} onChange={(e) => { setTargets(e.target.value); setPlan(undefined); }} placeholder="all (or e.g. web/*, functions)" />
        </label>
        <ErrorBox error={preview.error ?? deploy.error} />
        {plan && (
          <div className="plan">
            <p>
              {plan.targets.length} target{plan.targets.length === 1 ? "" : "s"} → <strong>{plan.environment}</strong>
              {plan.approvalRequired && <span className="tag warn">approval required</span>}
            </p>
            <ul>
              {plan.targets.map((t) => (
                <li key={t.address}>
                  <code>{t.address}</code> <span className="muted">→ {t.provider} {t.destination}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="form-actions">
          {!plan ? (
            <Button type="submit" busy={preview.busy}>
              Review
            </Button>
          ) : (
            <Button variant="primary" type="button" busy={deploy.busy} onClick={() => { void deploy.run(); }}>
              Deploy {plan.targets.length} target{plan.targets.length === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}

function RunCiForm({ project, commitSha, gitRef, onClose }: { project: ProjectType; commitSha: string; gitRef: string; onClose: () => void }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [sha, setSha] = useState(commitSha);
  const [targets, setTargets] = useState("");
  const run = useAction(async () => {
    const p = await fetcher<PipelineDetail>(`/v1/orgs/${org}/projects/${project.slug}/pipelines`, {
      method: "POST",
      body: { commitSha: sha, ref: gitRef.replace(/^refs\/heads\//, ""), ...(targets.trim() ? { targets: targets.split(",").map((t) => t.trim()) } : {}) },
    });
    void navigate(`/o/${org}/pipelines/${p.id}`);
  });
  return (
    <Card title="Run CI" actions={<button className="link-button" onClick={onClose}>Close</button>}>
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run.run();
        }}
      >
        <label>
          Commit
          <input value={sha} onChange={(e) => { setSha(e.target.value); }} required pattern="[0-9a-f]{40}" />
        </label>
        <label>
          Targets
          <input value={targets} onChange={(e) => { setTargets(e.target.value); }} placeholder="all" />
        </label>
        <ErrorBox error={run.error} />
        <div className="form-actions">
          <Button variant="primary" type="submit" busy={run.busy}>
            Run
          </Button>
        </div>
      </form>
    </Card>
  );
}
